'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface ConflictAlertBannerProps {
  onDismiss: () => void;
}

const DISMISS_KEY = 'netmanager_conflict_banner_dismissed_at';

export default function ConflictAlertBanner({ onDismiss }: ConflictAlertBannerProps) {
  const [conflictCount, setConflictCount] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check if dismissed within the last hour
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      if (elapsed < 60 * 60 * 1000) {
        // Still within 1-hour dismiss window — don't show
        return;
      }
    }

    const fetch = async () => {
      const { count } = await supabase
        .from('ip_addresses')
        .select('*', { count: 'exact', head: true })
        .eq('stato', 'conflitto');
      const n = count || 0;
      setConflictCount(n);
      if (n > 0) setVisible(true);
    };
    fetch();
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    onDismiss();
  };

  if (!visible || conflictCount === 0) return null;

  return (
    <div className="flex items-center gap-3 bg-red-500/8 border border-red-500/25 rounded-xl px-5 py-4">
      <Icon name="ExclamationTriangleIcon" size={18} className="text-red-400 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-base text-red-400 font-semibold">
          {conflictCount} conflitto/i IP rilevato/i — Intervento immediato richiesto
        </p>
        <p className="text-sm text-red-400/70 mt-0.5">
          Vai a Gestione Indirizzi IP per visualizzare i dettagli e risolvere i conflitti.
        </p>
      </div>
      <button
        onClick={handleDismiss}
        className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors flex-shrink-0"
        title="Nascondi per 1 ora"
      >
        <Icon name="XMarkIcon" size={16} className="text-red-400" />
      </button>
    </div>
  );
}